import React from "react";
import {
  FiDownload,
  FiBriefcase,
  FiBook,
  FiAward,
  FiCheckCircle,
} from "react-icons/fi";
import "./Resume.css";

const Resume = () => {
  const experience = [
    {
      id: 1,
      title: "Software Developer Intern",
      company: "Printzet Labs",
      location: "Remote",
      period: "July 2025 – Nov 2025",
      description:
        "Contributed to frontend development for production-grade web applications, collaborating with cross-functional teams to build scalable and user-friendly interfaces while following modern development best practices.",
      achievements: [
        "Developed and optimized responsive UI components using React and modern JavaScript",
        "Collaborated with designers to implement consistent design patterns and reusable components",
        "Improved application performance through code optimization and efficient state management",
        "Participated in code reviews and agile sprint cycles to ensure high-quality deliverables",
      ],
    },
  ];

  const education = [
    {
      id: 1,
      degree: "Bachelor of Technology in Computer Science & Engineering",
      institution: "Madan Mohan Malaviya University of Technology (MMMUT)",
      location: "Gorakhpur, India",
      period: "2022 – 2026",
      description:
        "Focused on software engineering, data structures, artificial intelligence, and full-stack development. Actively involved in technical projects, hackathons, and development-focused student initiatives.",
    },
    {
      id: 2,
      degree: "Secondary Education",
      institution: "Little Flower School",
      location: "Gorakhpur",
      period: "2020-2021",
      description:
        "Completed higher secondary education with a focus on Science and Mathematics. Built a strong analytical foundation through subjects such as Physics, Chemistry, and Mathematics, which sparked my interest in problem-solving and technology.",
    },
    {
      id: 3,
      degree: "High School Education",
      institution: "Little Flower School",
      location: "Gorakhpur",
      period: "2018-2019",
      description:
        "Completed secondary education with distinction, developing strong fundamentals in mathematics, logical reasoning, and core sciences. Actively participated in academic and extracurricular activities that nurtured discipline and curiosity for learning.",
    },
  ];

  const certifications = [
    {
      name: "Complete Data Science, Machine Learning, Deep Learning & NLP Bootcamp",
      issuer: "Udemy",
      year: "2025",
    },
    {
      name: "AWS Certified Cloud Practitioner",
      issuer: "Amazon Web Services",
      year: "2025",
    },
    {
      name: "Google Cloud Skill Boot",
      issuer: "Google",
      year: "2024",
    },
    {
      name: "Python for Data Science",
      issuer: "IBM",
      year: "2024",
    },
  ];

  const technicalSkills = [
    {
      category: "Frontend Development",
      skills: [
        "React.js",
        "Next.js",
        "TypeScript",
        "JavaScript (ES6+)",
        "Tailwind CSS",
        "HTML5",
        "CSS3",
      ],
    },
    {
      category: "Backend Development",
      skills: ["Node.js", "Express.js", "REST APIs", "JWT Authentication"],
    },
    {
      category: "Databases",
      skills: ["MongoDB", "MySQL"],
    },
    {
      category: "AI / Machine Learning",
      skills: ["TensorFlow", "Deep Learning"],
    },
    {
      category: "Tools & Platforms",
      skills: [
        "Git",
        "GitHub",
        "Docker (Basics)",
        "AWS (Cloud Fundamentals)",
        "Postman",
        "VS Code",
        "Figma",
      ],
    },
    {
      category: "Programming Language",
      skills: ["Java", "Python"],
    },
    {
      category: "Other Concepts",
      skills: [
        "Data Structures & Algorithms",
        "OOP",
        "System Design Basics",
        "Agile Methodology",
      ],
    },
  ];

  return (
    <section id="resume" className="resume section">
      <div className="container">
        <div className="section-header">
          <span className="section-subtitle">Resume</span>
          <h2 className="section-title">Experience & Skills</h2>
          <p className="section-description">
            My professional journey and the skills I've developed along the way
          </p>
        </div>

        {/* Download Resume Button */}
        <div className="resume-download">
          <a
            href="https://drive.google.com/file/d/17FGUKIip9Erxev0Pz4MaNZslzCOR7uC9/view?usp=sharing"
            download
            className="btn btn-primary"
          >
            <FiDownload />
            Download Full Resume
          </a>
        </div>

        <div className="resume-content">
          {/* Experience Section */}
          <div className="resume-section">
            <div className="resume-section-header">
              <FiBriefcase className="section-icon" />
              <h3>Work Experience</h3>
            </div>
            <div className="timeline">
              {experience.map((exp, index) => (
                <div
                  key={exp.id}
                  className="timeline-item fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="timeline-marker"></div>
                  <div className="timeline-content card">
                    <div className="timeline-header">
                      <div>
                        <h4 className="timeline-title">{exp.title}</h4>
                        <p className="timeline-company">{exp.company}</p>
                        <p className="timeline-location">{exp.location}</p>
                      </div>
                      <span className="timeline-period">{exp.period}</span>
                    </div>
                    <p className="timeline-description">{exp.description}</p>
                    <ul className="timeline-achievements">
                      {exp.achievements.map((achievement, i) => (
                        <li key={i}>
                          <FiCheckCircle className="check-icon" />
                          {achievement}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Education Section */}
          <div className="resume-section">
            <div className="resume-section-header">
              <FiBook className="section-icon" />
              <h3>Education</h3>
            </div>
            <div className="timeline">
              {education.map((edu, index) => (
                <div
                  key={edu.id}
                  className="timeline-item fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="timeline-marker"></div>
                  <div className="timeline-content card">
                    <div className="timeline-header">
                      <div>
                        <h4 className="timeline-title">{edu.degree}</h4>
                        <p className="timeline-company">{edu.institution}</p>
                        <p className="timeline-location">{edu.location}</p>
                      </div>
                      <span className="timeline-period">{edu.period}</span>
                    </div>
                    <p className="timeline-description">{edu.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Certifications */}
        <div className="certifications">
          <div className="resume-section-header centered">
            <FiAward className="section-icon" />
            <h3>Certifications</h3>
          </div>
          <div className="certifications-grid">
            {certifications.map((cert, index) => (
              <div
                key={index}
                className="certification-card card fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="cert-badge">
                  <FiAward />
                </div>
                <h4>{cert.name}</h4>
                <p className="cert-issuer">{cert.issuer}</p>
                <span className="cert-year">{cert.year}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Technical Skills */}
        <div className="technical-skills">
          <h3 className="skills-section-title">Technical Skills</h3>
          <div className="skills-categories">
            {technicalSkills.map((category, index) => (
              <div
                key={index}
                className="skill-category fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <h4 className="category-title">{category.category}</h4>
                <div className="category-skills">
                  {category.skills.map((skill, skillIndex) => (
                    <span key={skillIndex} className="skill-badge">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Resume;
